"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.crudProjectionXorRefine = exports.crudProjectionField = exports.crudProjectionListSchema = exports.isProjectionObjectSchema = exports.isProjectionJsonbSchema = exports.unwrapSchemaTypeName = exports.pruneSubsumedPaths = exports.splitProjectionPath = exports.isEmptyProjection = exports.DANGEROUS_PROJECTION_SEGMENTS = exports.CRUD_PROJECTION_SEGMENT_RE = exports.DEFAULT_CRUD_PROJECTION_LIMITS = exports.CRUD_PROJECTION_MAX_PATH_LENGTH = exports.CRUD_PROJECTION_MAX_DEPTH = exports.CRUD_PROJECTION_MAX_FIELDS = void 0;
const zod_1 = require("zod");
// Именованные константы — greppable, меняются в одном месте.
exports.CRUD_PROJECTION_MAX_FIELDS = 64;
exports.CRUD_PROJECTION_MAX_DEPTH = 8;
exports.CRUD_PROJECTION_MAX_PATH_LENGTH = 256;
exports.DEFAULT_CRUD_PROJECTION_LIMITS = {
    maxItems: exports.CRUD_PROJECTION_MAX_FIELDS,
    maxSegments: exports.CRUD_PROJECTION_MAX_DEPTH,
    maxItemLength: exports.CRUD_PROJECTION_MAX_PATH_LENGTH,
};
/** Сегмент пути: буквы/цифры/подчёркивание/дефис (UUID-ключи проходят). */
exports.CRUD_PROJECTION_SEGMENT_RE = /^[A-Za-z0-9_-]{1,128}$/;
/**
 * Сегменты, запрещённые в дот-путях (prototype-pollution): `__proto__` и т.п.
 * проходят `CRUD_PROJECTION_SEGMENT_RE`, но обычное присваивание `obj[seg]=v`
 * при реассемблинге поддерева отравило бы прототип процесса. Отбраковываются
 * грамматикой (эшелон 1), SQL-builder'ом (эшелон 2) И guard'ятся в
 * реассемблерах (projection-apply/projection-row) — defense-in-depth.
 */
exports.DANGEROUS_PROJECTION_SEGMENTS = new Set([
    '__proto__',
    'prototype',
    'constructor',
]);
/** «Пустая» проекция: null / undefined / [] — сервер трактует как «маски нет». */
const isEmptyProjection = (value) => value == null || (Array.isArray(value) && value.length === 0);
exports.isEmptyProjection = isEmptyProjection;
const splitProjectionPath = (item) => {
    const parts = item.split('.');
    return { head: parts[0] ?? '', segments: parts.slice(1) };
};
exports.splitProjectionPath = splitProjectionPath;
/**
 * Нормализация набора путей одного поля: дедуп + поглощение — путь,
 * префиксом которого является другой выбранный путь, избыточен
 * (`context.import` уже несёт `context.import.messageText`). Уменьшает
 * SQL (#>-алиасы/параметры). Семантику не меняет — чистая оптимизация.
 */
const pruneSubsumedPaths = (paths) => {
    const sorted = [...paths].map((p) => [...p]).sort((a, b) => a.length - b.length);
    const kept = [];
    for (const path of sorted) {
        const subsumed = kept.some((shorter) => shorter.length <= path.length && shorter.every((seg, i) => path[i] === seg));
        if (!subsumed)
            kept.push(path);
    }
    return kept;
};
exports.pruneSubsumedPaths = pruneSubsumedPaths;
/**
 * typeName схемы после снятия Optional/Nullable/Default. Сравниваем typeName, а не
 * instanceof: под vitest у пакета-потребителя может оказаться другой экземпляр zod.
 * Локальный unwrap — contracts-base не может зависеть от crud-runtime/zod-introspect
 * (направление зависимостей).
 */
const unwrapSchemaTypeName = (schema) => {
    let current = schema;
    for (;;) {
        const tn = current._def.typeName ?? '';
        if (tn === 'ZodOptional') {
            current = current.unwrap();
            continue;
        }
        if (tn === 'ZodNullable') {
            current = current.unwrap();
            continue;
        }
        if (tn === 'ZodDefault') {
            current = current.removeDefault();
            continue;
        }
        return tn;
    }
};
exports.unwrapSchemaTypeName = unwrapSchemaTypeName;
const isProjectionJsonbSchema = (schema) => {
    const tn = (0, exports.unwrapSchemaTypeName)(schema);
    return tn === 'ZodObject' || tn === 'ZodRecord';
};
exports.isProjectionJsonbSchema = isProjectionJsonbSchema;
/**
 * Строго типизированное jsonb-поле (`z.object({...})`, а не `z.record`) —
 * его вложенные required-ключи shallow `.partial()` не ослабляет, поэтому в
 * wire-форме reply такое поле заменяется терпимым record (I2 финального ревью,
 * см. define-entity.ts).
 */
const isProjectionObjectSchema = (schema) => (0, exports.unwrapSchemaTypeName)(schema) === 'ZodObject';
exports.isProjectionObjectSchema = isProjectionObjectSchema;
/**
 * Схема массива projection-элементов с точной валидацией: голова ∈
 * allowedHeads; путь глубже головы — только в jsonbHeads.
 */
const crudProjectionListSchema = (allowedHeads, jsonbHeads, limits = {}) => {
    const lim = { ...exports.DEFAULT_CRUD_PROJECTION_LIMITS, ...limits };
    return zod_1.z
        .array(zod_1.z.string().min(1).max(lim.maxItemLength))
        .min(1)
        .max(lim.maxItems)
        .superRefine((arr, ctx) => {
        arr.forEach((raw, index) => {
            const issue = (message) => {
                ctx.addIssue({ code: zod_1.z.ZodIssueCode.custom, message, path: [index] });
            };
            const parts = raw.split('.');
            if (parts.length > lim.maxSegments) {
                issue(`projection path '${raw}': too many segments (max ${lim.maxSegments})`);
                return;
            }
            if (parts.some((seg) => !exports.CRUD_PROJECTION_SEGMENT_RE.test(seg))) {
                issue(`projection path '${raw}': invalid segment`);
                return;
            }
            if (parts.some((seg) => exports.DANGEROUS_PROJECTION_SEGMENTS.has(seg))) {
                issue(`projection path '${raw}': reserved prototype key not allowed`);
                return;
            }
            const head = parts[0];
            if (!allowedHeads.includes(head)) {
                issue(`projection field '${head}' is not a snapshot field`);
                return;
            }
            if (parts.length > 1 && !jsonbHeads.includes(head)) {
                issue(`projection path '${raw}': field '${head}' is not a jsonb field`);
            }
        });
    });
};
exports.crudProjectionListSchema = crudProjectionListSchema;
/**
 * Поле проекции для query-схемы (`fields`/`fieldsOmit`): нормализует «пустоту»
 * (null / [] / undefined) в undefined ЧЕРЕЗ preprocess ДО валидации — сервер
 * трактует их единообразно как «маски нет» (клиент может слать плоско
 * `fields: filters.fields` без условных спредов, а `{fields:[], fieldsOmit:[…]}`
 * становится валидным omit-запросом, не двойной ошибкой). Непустой массив идёт
 * в `crudProjectionListSchema` (грамматика + whitelist). Возвращаемая схема уже
 * `.optional()` — снаружи оборачивать не надо.
 */
const crudProjectionField = (allowedHeads, jsonbHeads, limits = {}) => zod_1.z.preprocess((value) => ((0, exports.isEmptyProjection)(value) ? undefined : value), (0, exports.crudProjectionListSchema)(allowedHeads, jsonbHeads, limits).optional());
exports.crudProjectionField = crudProjectionField;
/** XOR fields/fieldsOmit — вешать `.superRefine(crudProjectionXorRefine)` на query-схему. */
const crudProjectionXorRefine = (val, ctx) => {
    if (val.fields !== undefined && val.fieldsOmit !== undefined) {
        ctx.addIssue({
            code: zod_1.z.ZodIssueCode.custom,
            message: 'fields and fieldsOmit are mutually exclusive',
            path: ['fieldsOmit'],
        });
    }
};
exports.crudProjectionXorRefine = crudProjectionXorRefine;
//# sourceMappingURL=projection-zod.js.map